import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import {
  CreditCard, Lock, ExternalLink, Check, Loader2, ShieldCheck, Zap, Crown, Rocket, Sparkles,
} from "lucide-react";

const ORANGE = "#ff8a3d";
const PLAN_ICON = { free: Rocket, pro: Zap, ultra: Crown };

function Toggle({ on, onClick, label, sub, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.6)] px-3.5 py-3 hover:bg-[var(--accent-a10)] transition-colors text-left"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white">{label}</div>
        {sub && <div className="text-[11px] text-[#8b949e] leading-tight">{sub}</div>}
      </div>
      <span
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? "bg-[var(--accent)]" : "bg-[#2a3a4a]"}`}
        style={on ? { boxShadow: "0 0 14px var(--accent)" } : {}}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function KeyField({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <label className="font-mono-hud text-[9px] tracking-[0.2em] uppercase mb-1 block" style={{ color: ORANGE }}>
        {label}
      </label>
      <div className="flex items-center gap-2 clip-corner border px-3 py-2.5"
        style={{ borderColor: "rgba(255,138,61,0.4)", background: "rgba(255,138,61,0.05)" }}>
        <Lock className="h-3.5 w-3.5 shrink-0" style={{ color: ORANGE }} />
        <code className="min-w-0 flex-1 truncate font-mono text-[12px] text-[#e8d3c2]">{value || "—"}</code>
      </div>
    </div>
  );
}

export function StripeSubscriptions() {
  const [cfg, setCfg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [trialOn, setTrialOn] = useState(true);
  const [paidOn, setPaidOn] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/stripe"), api.get("/billing/plans")])
      .then(([c, p]) => { setCfg(c.data); setPlans(p.data.plans || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const checkout = async (lookup_key, id) => {
    setActing(id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe");
      setActing(null);
    }
  };

  const startTrial = async () => {
    setActing("free");
    try {
      await api.post("/billing/start-trial");
      toast.success("Essai gratuit de 7 jours activé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Essai indisponible");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="hud-panel clip-corner p-8 flex items-center justify-center" data-testid="stripe-subscriptions-panel">
        <Loader2 className="h-6 w-6 animate-spin accent-text" />
      </div>
    );
  }

  const live = cfg?.live;
  return (
    <div
      data-testid="stripe-subscriptions-panel"
      className="relative overflow-hidden rounded-2xl border border-white/10 p-5 sm:p-6"
      style={{
        background:
          "linear-gradient(135deg, rgba(6,10,24,0.92), rgba(3,7,18,0.96)), radial-gradient(circle at 15% 10%, rgba(255,138,61,0.10), transparent 40%), radial-gradient(circle at 85% 90%, rgba(0,224,255,0.10), transparent 40%)",
        backdropFilter: "blur(16px)",
      }}
    >
      {/* circuit texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />

      <div className="relative">
        <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "rgba(255,138,61,0.14)", border: `1px solid ${ORANGE}` }}>
              <CreditCard className="h-4.5 w-4.5" style={{ color: ORANGE }} />
            </span>
            <div>
              <h3 className="font-display text-lg font-black tracking-[0.1em] text-white text-glow">STRIPE &amp; SUBSCRIPTIONS</h3>
              <p className="font-mono-hud text-[10px] tracking-[0.18em] text-[#8b949e] uppercase">Paiements &amp; abonnements</p>
            </div>
          </div>
          <span
            data-testid="stripe-mode-badge"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono-hud text-[10px] uppercase tracking-[0.16em]"
            style={live
              ? { color: "#00ffaa", background: "rgba(0,255,170,0.12)" }
              : { color: ORANGE, background: "rgba(255,138,61,0.12)" }}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {live ? "En réel" : "Mode test"}
          </span>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_1.4fr]">
          {/* left — connection + plan toggles */}
          <div className="space-y-4">
            <div className="space-y-3">
              <KeyField label="Publishable Key" value={cfg?.publishable_key_masked} testid="stripe-pub-field" />
              <KeyField label="Secret Key" value={`${live ? "sk_live" : "sk_test"}_•••••••••••• (gérée)`} testid="stripe-secret-field" />
              <p className="text-[11px] leading-snug text-[#8b949e]">
                Clés <b style={{ color: ORANGE }}>gérées automatiquement</b> — rien à coller. Réclame ton compte pour encaisser en réel.
              </p>
              {cfg?.onboarding_url && (
                <a
                  href={cfg.onboarding_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="stripe-claim-button"
                  className="inline-flex w-full items-center justify-center gap-2 clip-corner py-2.5 text-sm font-bold text-black transition-transform hover:scale-[1.02]"
                  style={{ background: ORANGE }}
                >
                  <ExternalLink className="h-4 w-4" /> {live ? "Gérer mon compte Stripe" : "Passer en réel — Réclamer mon compte"}
                </a>
              )}
            </div>

            <div className="pt-2">
              <div className="font-mono-hud text-[10px] tracking-[0.22em] text-[#8b949e] uppercase mb-2.5">Subscription Plans</div>
              <div className="space-y-2.5">
                <Toggle testid="plan-toggle-trial" on={trialOn} onClick={() => setTrialOn((v) => !v)}
                  label="Essai gratuit (7 jours)" sub="Débloque Pro sans carte bancaire" />
                <Toggle testid="plan-toggle-paid" on={paidOn} onClick={() => setPaidOn((v) => !v)}
                  label="Abonnements payants" sub="Pro & Enterprise via Stripe" />
              </div>
            </div>
          </div>

          {/* right — pricing cards */}
          <div className="grid gap-3 sm:grid-cols-3">
            {plans.map((p) => {
              const Icon = PLAN_ICON[p.id] || Sparkles;
              const isFree = p.id === "free";
              const disabled = (isFree && !trialOn) || (!isFree && !paidOn);
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  className={`relative flex flex-col rounded-xl border p-4 transition-all ${
                    p.id === "pro" ? "border-[var(--accent)]" : "border-white/10"
                  }`}
                  style={{
                    background: "rgba(2,8,18,0.6)",
                    boxShadow: p.id === "pro" ? "0 0 24px rgba(0,224,255,0.15)" : "none",
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  {p.id === "pro" && (
                    <span className="absolute -top-2 right-3 rounded-full bg-[var(--accent)] px-2 py-0.5 font-mono-hud text-[9px] uppercase tracking-wider text-black">
                      Populaire
                    </span>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Icon className="h-4 w-4 accent-text" />
                    <span className="font-display text-base font-black text-white">{isFree ? "Free" : p.name}</span>
                  </div>
                  <div className="mt-2 font-display text-2xl font-black accent-text">
                    {isFree ? "Essai 7 j" : `${p.price_eur} €`}
                    {!isFree && <span className="font-mono-hud text-[11px] text-[#6b7a8d]"> /mois</span>}
                  </div>
                  <ul className="mt-3 flex-1 space-y-1.5">
                    {(p.features || []).slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#9fb2c6]">
                        <Check className="mt-0.5 h-3 w-3 shrink-0 accent-text" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    data-testid={`activate-${p.id}`}
                    disabled={disabled || acting === p.id}
                    onClick={() => (isFree ? startTrial() : checkout(p.lookup_key, p.id))}
                    className="mt-3 clip-corner w-full py-2 text-[11px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
                    style={{
                      background: "var(--accent)", color: "#02121c",
                    }}
                  >
                    {acting === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isFree ? "Activer l'essai" : "S'abonner"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {cfg?.tax_note && (
          <p className="relative mt-4 font-mono-hud text-[10px] text-[#6b7a8d] tracking-wide">{cfg.tax_note}</p>
        )}
      </div>
    </div>
  );
}
